<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import type { iconName, iconSize } from "@/Modules/App/Models/Styles/Icons";
import type { InputType, ValueType } from "@/Common/Models/Ui/Input";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { hasValue } from "@/Utils/Objects";
import { isString, isValidString, capitalizeFirstLetter } from "@/Utils/String";
import { isApp } from "@/Utils/Ionic";

type Props = {
  debounceMillis?: number;
  endIcon?: iconName;
  endIconSize?: iconSize;
  errorMessage?: string;
  extraMessage?: string;
  gutterBottom?: boolean;
  hasCounter?: boolean;
  hasDebounce?: boolean;
  hasInitialFocus?: boolean;
  inputClass?: string;
  inputId?: string;
  isDisabled?: boolean;
  label?: string;
  max?: string;
  maxLength?: number;
  min?: string;
  modelValue: ValueType;
  placeholder?: string;
  startIcon?: iconName;
  startIconSize?: iconSize;
  type?: InputType;
  isPasteEnabled?: boolean;
  isHighlitedTitle?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  debounceTimeMillis: 500,
  endIconSize: "md",
  gutterBottom: true,
  hasCounter: false,
  hasDebounce: true,
  hasInitialFocus: false,
  isDisabled: false,
  startIconSize: "md",
  type: "text",
  isPasteEnabled: true,
  isHighlitedTitle: false,
});

const internalValue = ref<ValueType>(props.modelValue);
const isPasswordShown = ref<boolean>(false);

const emit = defineEmits<{
  (event: "update:modelValue", value: ValueType): void;
  (event: "change", value: ValueType): void;
  (event: "blur"): void;
  (event: "focus"): void;
  (event: "onSubmit"): void;
  (event: "clickEndIcon"): void;
  (event: "clickStartIcon"): void;
  (event: "error", hasError: boolean): void;
  (event: "focusOut"): void;
}>();

const inputMode = computed(
  (): undefined | "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search" => {
    if (props.type === "number") return "numeric";
    else if (props.type === "email") return "email";
    else if (props.type === "text") return "text";

    return undefined;
  }
);

const hasError = computed(() => isValidString(props.errorMessage));

const hasLabel = computed(() => isValidString(props.label));

const charCount = computed((): string | undefined => {
  if (props.hasCounter) {
    if (!hasValue(internalValue.value) || !isString(internalValue.value)) {
      return `${0}/${props.maxLength}`;
    }

    if (!hasValue(props.maxLength)) {
      return internalValue.value.length.toString();
    }

    if (props.maxLength < internalValue.value.length) {
      return `${props.maxLength - internalValue.value.length}/${props.maxLength}`;
    }

    return `${internalValue.value.length}/${props.maxLength}`;
  }

  return undefined;
});

const hasEndIcon = computed(() => {
  if (isValidString(props.endIcon)) {
    if (props.type !== "password" && isApp()) {
      return false;
    }

    return (["password", "date", "time", "text"] as InputType[]).includes(props.type);
  } else if (props.type === "password") {
    return true;
  }

  return false;
});

const endIconName = computed((): iconName => {
  if (isValidString(props.endIcon)) return props.endIcon;

  switch (props.type) {
    case "password":
      return isPasswordShown.value ? "EyeOn" : "EyeOff";
    case "date":
      return "Calendar";
    case "time":
      return "Clock";
    default:
      return "" as iconName;
  }
});

const onChange = () => {
  if (props.isDisabled) return;

  emit("update:modelValue", internalValue.value);
  emit("change", internalValue.value);
};

const onSubmit = () => {
  emit("onSubmit");
};

const onBlur = () => {
  emit("blur");
};

const onFocus = () => {
  emit("focus");
};

const onFocusOut = () => {
  emit("focusOut");
};

const onClickStartIcon = () => emit("clickStartIcon");

const onClickEndIcon = () => {
  if (!isValidString(props.endIcon) && props.type === "password") {
    isPasswordShown.value = !isPasswordShown.value;
  }

  emit("clickEndIcon");
};

const deleteWatch = watch(
  () => props.modelValue,
  () => {
    internalValue.value = props.modelValue;
  }
);

onMounted(() => {
  internalValue.value = props.modelValue;
});

onUnmounted(() => {
  deleteWatch();
});
</script>

<template>
  <div
    class="textfield"
    :class="{ error: hasError, disabled: !!props.isDisabled, 'mb-4': props.gutterBottom }"
  >
    <label
      v-if="hasLabel"
      class="textfield__label"
      :class="{ 'textfield__label--highlited': props.isHighlitedTitle }"
    >
      {{ label }}
    </label>

    <div
      class="textfield__input"
      :class="{
        'textfield__input--password': props.type === 'password',
        'textfield__input--start_icon': hasValue(props.startIcon),
        'textfield__input--end_icon': hasValue(props.endIcon) || props.type === 'password',
        'textfield__input--date_icon': props.type === 'dateIcon',
      }"
    >
      <TcIcon
        v-if="hasValue(props.startIcon)"
        class="textfield__input__start_icon"
        :size="props.startIconSize"
        :name="props.startIcon"
        color="content-high"
        @click="onClickStartIcon"
      />

      <template v-if="props.type === 'time'">
        <input
          v-model="internalValue"
          type="time"
          :disabled="props.isDisabled"
          :class="{ 'has-value': isValidString(internalValue), clean_decoration: isApp() }"
          @input="onChange"
          @keyup="onChange"
          @keyup.enter="onSubmit"
          @blur="onBlur"
          @focus="onFocus"
          @focusout="onFocusOut"
        />
      </template>

      <template v-else-if="props.type === 'date' || props.type === 'dateIcon'">
        <input
          v-model="internalValue"
          type="date"
          :disabled="props.isDisabled"
          :min="props.min"
          :max="props.max"
          :class="{ 'has-value': isValidString(internalValue), clean_decoration: isApp() }"
          @input="onChange"
          @keyup="onChange"
          @keyup.enter="onSubmit"
          @blur="onBlur"
          @focus="onFocus"
          @focusout="onFocusOut"
        />
      </template>

      <input
        v-else
        v-model="internalValue"
        :inputmode="inputMode"
        :type="isPasswordShown ? 'text' : props.type"
        :disabled="props.isDisabled"
        :placeholder="capitalizeFirstLetter(props.placeholder)"
        :class="inputClass"
        :id="inputId"
        :min="props.min"
        :max="props.max"
        :onpaste="() => isPasteEnabled"
        @input="onChange"
        @keyup="onChange"
        @keyup.enter="onSubmit"
        @blur="onBlur"
        @focus="onFocus"
        @focusout="onFocusOut"
      />

      <TcIcon
        v-if="hasEndIcon"
        :name="endIconName"
        class="textfield__input__end_icon"
        :color="hasError ? 'semantic-error' : undefined"
        :size="props.endIconSize"
        @click="onClickEndIcon"
      />
    </div>

    <div class="textfield__info" :class="{ error: hasError }">
      <div v-if="hasError || isValidString(props.extraMessage)" class="textfield__info__text">
        <span v-if="hasError" class="textfield__info__text-error">
          {{ errorMessage }}
        </span>

        <span v-else-if="isValidString(props.extraMessage)">
          {{ props.extraMessage }}
        </span>
      </div>

      <div
        v-if="props.type === 'text' && hasCounter"
        class="textfield__info__counter"
        :class="{ error: hasError }"
      >
        <span>
          {{ charCount }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.date_modal {
  @include colors.bg(background, overlay);

  height: 100vh;
  width: 100vw;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10000;

  &__content {
    @include colors.bg(background, base);
    @include radius(s);
    @include sp.margin(x, auto);

    position: relative;
    z-index: 10002;
    overflow: hidden;
    width: fit-content;
    min-width: 300px;
    max-width: calc(100vw - $space-4 * 2);
    margin-top: 15vh;

    &__picker {
      max-width: 100%;
    }
  }

  &__wrapper {
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 10001;
  }
}

.textfield {
  display: block;
  width: 100%;

  &__label {
    @include text.general(low, l);
    @include colors.color(content, high);
    @include sp.padding(b, 1);

    display: block;

    &--highlited {
      @include text.general(high, s);
    }
  }

  &__input {
    @include text.general(low, l);
    @include colors.color(content, high);

    position: relative;
    width: 100%;
    height: $size-input-height;
    border: none;

    .input {
      display: flex !important;

      &__value {
        flex-grow: 2;
      }
    }

    input,
    .input {
      @include sp.padding(x, 4);
      @include sp.padding(y, 3);
      @include text.general(low, l);
      @include colors.color(background, transparent);
      @include colors.border(content, low, true);
      @include colors.bg(background, base);
      @include border(1, solid);
      @include radius(s);

      display: block;
      resize: none;
      width: 100%;
      text-align: left;
      height: $size-input-height;

      &::placeholder {
        @include colors.color(content, mid);
      }

      &::-webkit-date-and-time-value {
        text-align: left;
      }
    }

    input {
      &:hover + .textfield__input__end_icon,
      &:focus + .textfield__input__end_icon {
        visibility: visible;
      }

      &:focus-visible {
        outline: none;
      }

      &:focus {
        @include border(1, solid);
        @include colors.border(content, high, true);
      }

      &.clean_decoration {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        -o-appearance: none;
      }
    }

    input[type="time"] {
      &::-webkit-calendar-picker-indicator {
        opacity: 1;
        color: rgba(0, 0, 0, 0);
        background: url("@/Assets/icons/clock.svg") no-repeat;
        background-position: center center;
      }
    }

    input[type="date"],
    input[type="dateIcon"] {
      &::-webkit-calendar-picker-indicator {
        opacity: 1;
        color: rgba(0, 0, 0, 0);
        background: url("@/Assets/icons/calendar.svg") no-repeat;
        background-position: center center;
      }
    }

    &__end_icon,
    &__start_icon {
      position: absolute;
      top: 50%;
      -ms-transform: translateY(-50%);
      transform: translateY(-50%);
      cursor: pointer;
    }

    &__start_icon {
      left: 16px;
    }

    &__end_icon {
      right: 16px;
    }

    &--start_icon {
      input {
        @include sp.padding(l, 10);
      }
    }

    &--end_icon {
      input {
        @include sp.padding(r, 10);
      }
    }

    &--date_icon {
      input {
        width: auto;
        border: none;

        &:focus {
          border: none;
        }

        &::-webkit-datetime-edit {
          display: none;
          outline: none;
        }
      }
    }
  }

  &__info {
    display: flex;
    align-items: center;

    &__text,
    &__counter {
      @include text.general(low, s);
      @include colors.color(content, mid);
      @include sp.padding(t, 1);

      display: block;
    }

    &__text {
      flex-grow: 1;

      &-error {
        @include colors.color(semantic, error);
      }
    }

    &__counter {
      @include text.modification(right);

      flex-grow: 2;

      &.error {
        @include colors.color(semantic, error);
      }
    }

    &.error {
      @include colors.color(semantic, error);
    }
  }

  &.disabled {
    .textfield__input {
      cursor: not-allowed;

      input {
        @include colors.color(content, low);
      }
    }
  }

  &.error {
    .textfield__input input {
      @include border(1, solid);
      @include colors.border(semantic, error);
      @include colors.color(semantic, error);

      &:focus {
        @include colors.color(content, high);
      }

      &:focus-visible {
        outline-color: $color-semantic-error;
        outline-width: 0;
      }
    }
  }
}
</style>
